import {
  FloatingArrow,
  FloatingPortal,
  Placement,
  arrow,
  autoUpdate,
  flip,
  hide,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import { token } from '@porsche-code/kado-styled-system/tokens';
import { Fragment, useState } from 'react';

const Content = styled('div', {
  base: {
    backgroundColor: 'background.canvas',
    borderRadius: 'xl',
    zIndex: 'tooltip',
    maxWidth: '40%',
    shadow: 'md',
  },
});

const Trigger = styled('span', {});

const Spacer = styled('div', {
  base: {
    display: 'inline-block',
    py: 'sm',
    px: 'md',
    color: 'text.primary',
  },
});

export const Tooltip = ({
  children,
  className,
  content,
  placement = 'top',
  safePolygonClose,
  disabled,
  withPortal,
}: {
  children: React.ReactNode;
  className?: string;
  content: React.ReactNode;
  placement?: Placement;
  safePolygonClose?: boolean;
  disabled?: boolean;
  withPortal?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [arrowElement, setArrowElement] = useState<SVGSVGElement | null>(null);

  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context,
    placement: floatingPlacement,
    middlewareData,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
      arrow({ element: arrowElement }),
      hide(),
    ],
  });

  const hover = useHover(context, {
    move: false,
    handleClose: safePolygonClose ? safePolygon() : null,
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context, {
    bubbles: {
      escapeKey: true,
    },
  });
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const MaybePortal = withPortal ? FloatingPortal : Fragment;

  return (
    <>
      <Trigger
        className={className}
        ref={setReference}
        {...getReferenceProps()}
      >
        {children}
      </Trigger>

      {isOpen && !disabled && (
        <MaybePortal>
          <Content
            ref={setFloating}
            style={{
              ...floatingStyles,
              visibility: middlewareData.hide?.referenceHidden
                ? 'hidden'
                : 'visible',
            }}
            {...getFloatingProps()}
          >
            <Spacer>{content}</Spacer>
            <FloatingArrow
              ref={setArrowElement}
              context={context}
              fill={token('colors.background.canvas')}
              staticOffset={
                floatingPlacement.endsWith('-start') ||
                floatingPlacement.endsWith('-end')
                  ? '10%'
                  : null
              }
            />
          </Content>
        </MaybePortal>
      )}
    </>
  );
};
